<script setup lang="ts">
import { computed, defineAsyncComponent, watch } from 'vue';
import { useRouter } from 'vue-router';
import AuthPanel from '@/features/auth/AuthPanel.vue';
import LorUsAuthPanel from '@/features/auth/LorUsAuthPanel.vue';
import OroMxAuthPanel from '@/features/auth/OroMxAuthPanel.vue';
import { t } from '@/shared/i18n/messages';
import { activeSkinModule, isGc987Skin, isLorUsSkin, isOroMxSkin } from '@/shared/theme/active-skin';
import { useAppToastStore } from '@/stores/app-toast-store';
import { useAuthModalStore } from '@/stores/auth-modal-store';

const authModal = useAuthModalStore();
const appToast = useAppToastStore();
const router = useRouter();

// B-lite：注册了 authPanel 的皮肤走单一出口；旧 4 套皮肤仍走下面的 isXxxSkin 分支。
const SkinAuthPanel = activeSkinModule?.authPanel ? defineAsyncComponent(activeSkinModule.authPanel) : null;
const skinAuthModalClass = activeSkinModule?.authModalClass ?? '';
const skinAuthTeleport = activeSkinModule?.authModalTeleport || 'body';
/** Rosa 已在 .app-frame 内，禁止 Teleport 到自身，否则弹层会从 DOM 消失。 */
const disableAuthTeleport = skinAuthTeleport !== 'body';
const skinAuthPanelKey = computed(
  () => `skin-${authModal.referralCode}-${authModal.agentCode}`,
);

function showToast(message: string, kind: 'info' | 'error' = 'error') {
  appToast.show(message, kind);
}

// 有 pendingPath（goGated / 受限路由拦截）则回目标页；否则回主页。
async function handleSuccess() {
  const pending = authModal.consumePendingPath();
  authModal.closeAuth();
  const target = pending || '/';
  const current = router.currentRoute.value;
  if (current.fullPath !== target && current.path !== target) {
    await router.push(target);
  }
}

// 弹层打开时锁定背景滚动，关闭时恢复
watch(
  () => [authModal.open, authModal.notice] as const,
  ([open, notice]) => {
    document.body.style.overflow = open ? 'hidden' : '';
    const frame = document.querySelector('.app-frame') as HTMLElement | null;
    if (frame) frame.style.overflow = open ? 'hidden' : '';
    if (open && notice) showToast(notice, 'info');
  },
);
</script>

<template>
  <Teleport :to="skinAuthTeleport" :disabled="disableAuthTeleport">
    <Transition name="auth-modal">
      <div
        v-if="authModal.open"
        class="auth-modal"
        :class="[
          skinAuthModalClass,
          {
            'auth-modal--gc987': isGc987Skin,
            'auth-modal--lor-us': isLorUsSkin,
            'auth-modal--oro-mx': isOroMxSkin,
          },
        ]"
        role="dialog"
        aria-modal="true"
        :aria-label="isOroMxSkin ? t('oro.header.login') : isLorUsSkin ? 'Sign In' : 'Acesso'"
      >
        <button
          v-if="!isLorUsSkin"
          class="auth-modal__backdrop"
          type="button"
          :aria-label="t(isOroMxSkin ? 'oro.common.close' : 'auth.action.close')"
          @click="authModal.closeAuth()"
        />
        <div class="auth-modal__panel">
          <SkinAuthPanel
            v-if="SkinAuthPanel"
            :key="skinAuthPanelKey"
            :mode="authModal.mode"
            :referral-code="authModal.referralCode"
            :agent-code="authModal.agentCode"
            @close="authModal.closeAuth()"
            @success="handleSuccess"
            @toast="showToast"
          />
          <LorUsAuthPanel
            v-else-if="isLorUsSkin"
            :key="'lor-' + authModal.mode + authModal.referralCode + authModal.agentCode"
            :mode="authModal.mode"
            :referral-code="authModal.referralCode"
            @close="authModal.closeAuth()"
            @success="handleSuccess"
          />
          <OroMxAuthPanel
            v-else-if="isOroMxSkin"
            :key="'oro-' + authModal.mode + authModal.referralCode + authModal.agentCode"
            :mode="authModal.mode"
            :referral-code="authModal.referralCode"
            :agent-code="authModal.agentCode"
            @close="authModal.closeAuth()"
            @success="handleSuccess"
            @toast="showToast"
          />
          <AuthPanel
            v-else
            :key="authModal.mode + authModal.referralCode + authModal.agentCode"
            :mode="authModal.mode"
            :referral-code="authModal.referralCode"
            :agent-code="authModal.agentCode"
            @close="authModal.closeAuth()"
            @success="handleSuccess"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
